import { useEffect, useRef, useState } from "react";

import { Badge, Button, Icon, LegacyCard, LegacyStack, Text, TextField } from "@shopify/polaris";
import { ImageMajor } from "@shopify/polaris-icons";

import { checkMediaUrl, MediaType } from "@vendor-app/utils/validateMediaUrl";

interface Props {
  value: string;
  onChange: (value: string) => void;
}

const VALIDATION_DEBOUNCE_MS = 600;

export function MediaUrlField({ value, onChange }: Props) {
  const [error, setError] = useState<string | undefined>();
  const [mediaType, setMediaType] = useState<MediaType | undefined>();
  const [mediaLoadError, setMediaLoadError] = useState(false);

  const validationTokenRef = useRef(0);

  const validateUrl = async (urlValue: string) => {
    if (!urlValue) {
      return;
    }

    const token = ++validationTokenRef.current;
    const result = await checkMediaUrl(urlValue);
    if (token !== validationTokenRef.current) {
      return;
    }

    setError(result?.error);
    setMediaType(result?.mediaType);
    setMediaLoadError(!!result?.error);
  };

  const showPreview = Boolean(value && !error && mediaType && !mediaLoadError);

  useEffect(() => {
    setError(undefined);
    setMediaType(undefined);
    setMediaLoadError(false);

    if (!value) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void validateUrl(value);
    }, VALIDATION_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [value]);

  return (
    <LegacyStack vertical>
      <LegacyStack vertical spacing="extraTight">
        <LegacyStack distribution="equalSpacing">
          <Text variant="headingSm" as="h4">
            Media URL
          </Text>
          <Badge status="new">Optional</Badge>
        </LegacyStack>
        <LegacyStack vertical spacing="extraTight">
          <Text variant="bodySm" as="span" color="subdued">
            Supported: JPG, PNG, GIF, WebP, MP4, MOV.
          </Text>
          <Text variant="bodySm" as="span" color="subdued">
            Image File Size Limit: 5MB
          </Text>
          <Text variant="bodySm" as="span" color="subdued">
            Video File Size Limit: 50MB
          </Text>
        </LegacyStack>
        <TextField
          autoComplete="off"
          label=""
          value={value}
          onChange={newValue => onChange(newValue)}
          placeholder="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=600"
          error={error}
        />
      </LegacyStack>
      {value ? (
        <LegacyCard sectioned subdued>
          {showPreview ? (
            <div style={{ maxHeight: 300, display: "flex", justifyContent: "center" }}>
              {mediaType === "video" ? (
                <video
                  data-testid="video-preview"
                  src={value}
                  controls
                  preload="metadata"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  onError={() => setMediaLoadError(true)}
                />
              ) : (
                <img
                  src={value}
                  alt="Media preview"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  onError={() => setMediaLoadError(true)}
                />
              )}
            </div>
          ) : (
            <LegacyStack distribution="center" alignment="center">
              <Icon source={ImageMajor} />
              <Text as="p" variant="bodySm">
                Unable to load preview. Media will gracefully hide if it can&apos;t render.
              </Text>
            </LegacyStack>
          )}
        </LegacyCard>
      ) : null}

      <LegacyStack distribution="trailing">
        {!!value && (
          <Button plain destructive onClick={() => onChange("")}>
            Remove Media
          </Button>
        )}
      </LegacyStack>
      {mediaLoadError ? (
        <Text variant="bodySm" as="span" color="critical">
          Media could not be loaded. Check that the URL is publicly accessible.
        </Text>
      ) : null}
    </LegacyStack>
  );
}
